import { forwardRef, useId, useState, useEffect, useRef, useCallback } from 'react';
import styled from '@emotion/styled';

import { Message } from '../Message';

import type { InputProps, FontSizeValue } from './Input.types';
import type { Theme } from '../../theme';

/**
 * Font size token to CSS value mapping
 */
const FONT_SIZE_TOKENS: Record<string, string> = {
  xs: 'var(--font-size-xs, 12px)',
  sm: 'var(--font-size-sm, 14px)',
  base: 'var(--font-size-base, 14px)',
  md: 'var(--font-size-md, 14.5px)',
  lg: 'var(--font-size-lg, 15px)',
  xl: 'var(--font-size-xl, 18px)',
};

/**
 * Resolve font size value to CSS string
 */
const resolveFontSize = (fontSize: FontSizeValue | undefined): string | undefined => {
  if (!fontSize) return undefined;
  
  // Check if it's a theme token key
  if (typeof fontSize === 'string' && fontSize in FONT_SIZE_TOKENS) {
    return FONT_SIZE_TOKENS[fontSize];
  }
  
  // Otherwise, treat as CSS value
  return typeof fontSize === 'number' ? `${fontSize}px` : String(fontSize);
};

/**
 * Input component - A versatile input field for user data entry
 * Aligned with ib-ui TextField component for compatibility
 *
 * @example
 * ```tsx
 * <Input
 *   label="Email"
 *   type="email"
 *   placeholder="user@example.com"
 *   required
 * />
 *
 * <Input
 *   label="Password"
 *   type="password"
 *   error
 *   errorMessage="Password is required"
 * />
 *
 * <Input
 *   label="Search"
 *   type="search"
 *   leftElement={<SearchIcon />}
 *   rightElement={<ClearButton />}
 *   debounceTimeout={300}
 *   onChangeDebounced={(value) => search(value)}
 * />
 *
 * <Input
 *   label="Phone"
 *   mask={(v) => v.replace(/\D/g, '').slice(0, 10)}
 *   placeholder="(555) 555-5555"
 * />
 * ```
 */

const Wrapper = styled.div<{ $fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  ${({ $fullWidth }) => $fullWidth && 'width: 100%;'}
`;

const Label = styled.label<{ theme?: Theme }>`
  font-style: normal;
  font-weight: 600;
  display: block;
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: ${({ theme }) => theme.typography.lineHeight.normal};
  margin-bottom: 4px;
`;

const Required = styled.span<{ theme?: Theme }>`
  color: var(--color-highlight, var(--colour-highlight, ${({ theme }) => theme?.colors?.highlight || '#F4770B'}));
  margin-left: 2px;
`;

const InputContainer = styled.div<{
  theme?: Theme;
  $inputSize: 'sm' | 'md' | 'lg';
  $variant: 'default' | 'filled' | 'outlined' | 'display';
  $error: boolean;
  $disabled: boolean;
  $hasLeft: boolean;
  $hasRight: boolean;
  $borderRadius?: string;
  $focusColor?: string;
}>`
  position: relative;
  display: flex;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.background.default};
  border-radius: ${({ theme, $borderRadius }) => $borderRadius || theme.borders.radius.sm};
  transition: border-color 0.2s, box-shadow 0.2s;
  
  /* Size variants (sm: 32px, md: 36px) - aligned with Select, Multiselect, and Button */
  ${({ $inputSize }) => {
    switch ($inputSize) {
      case 'sm':
        return 'height: 32px;';
      default:
        return 'height: 36px;';
    }
  }}
  
  /* Visual variants - Default */
  ${({ $variant, theme, $error, $focusColor }) => {
    if ($variant === 'default') {
      return `
        border: ${theme.borders.width.thin} solid ${$error ? theme.colors.status.error : theme.colors.border.medium};
        
        &:hover:not(:has(input:disabled)) {
          border-color: ${$error ? theme.colors.status.error : ($focusColor || theme.colors.text.primary)};
        }
        
        &:focus-within {
          border-color: ${$error ? theme.colors.status.error : ($focusColor || theme.colors.text.primary)};
          ${$focusColor && !$error ? `box-shadow: 0 0 0 2px ${$focusColor}20;` : ''}
        }
      `;
    }
    
    if ($variant === 'filled') {
      return `
        background-color: ${theme.colors.background.disabled};
        border: ${theme.borders.width.thin} solid transparent;
        
        &:hover:not(:has(input:disabled)) {
          background-color: ${theme.colors.background.hover};
        }
        
        &:focus-within {
          background-color: ${theme.colors.background.default};
          border-color: ${$error ? theme.colors.status.error : ($focusColor || theme.colors.highlight)};
          ${$focusColor && !$error ? `box-shadow: 0 0 0 2px ${$focusColor}20;` : ''}
        }
      `;
    }
    
    if ($variant === 'outlined') {
      return `
        border: ${theme.borders.width.medium} solid ${$error ? theme.colors.status.error : theme.colors.border.medium};
        
        &:hover:not(:has(input:disabled)) {
          border-color: ${$error ? theme.colors.status.error : ($focusColor || theme.colors.text.primary)};
        }
        
        &:focus-within {
          border-color: ${$error ? theme.colors.status.error : ($focusColor || theme.colors.highlight)};
          ${$focusColor && !$error ? `box-shadow: 0 0 0 2px ${$focusColor}20;` : ''}
        }
      `;
    }

    if ($variant === 'display') {
      return `
        background-color: ${theme.colors.grey[150]};
        border: ${theme.borders.width.thin} solid ${theme.colors.grey[150]};
        cursor: default;
        
        &:hover {
          border-color: ${theme.colors.grey[150]};
        }
        
        &:focus-within {
          border-color: ${theme.colors.grey[150]};
        }
      `;
    }
  }}
  
  /* Disabled state - but not for display variant which has its own styling */
  ${({ $disabled, $variant, theme }) => $disabled && $variant !== 'display' && `
    opacity: 0.5;
    cursor: not-allowed;
    background-color: ${theme.colors.background.disabled};
  `}
  
  /* Active state (for ib-ui compatibility) */
  &.active {
    border-color: ${({ theme, $error }) => $error ? theme.colors.status.error : theme.colors.text.primary};
  }
`;

const StyledInput = styled.input<{
  theme?: Theme;
  $hasLeft: boolean;
  $hasRight: boolean;
  $inputFontSize?: string;
  $inputSize: 'sm' | 'md';
}>`
  flex: 1;
  width: 100%;
  border: none;
  background: transparent;
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-weight: ${({ theme }) => theme.typography.fontWeight.regular};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
  outline: none;
  /* Default font size based on size prop (sm: xs/12px, md: sm/14px), matching Select/Multiselect */
  font-size: ${({ $inputFontSize, $inputSize, theme }) =>
    $inputFontSize || ($inputSize === 'sm' ? theme.typography.fontSize.xs : theme.typography.fontSize.sm)};
  
  /* Production styles from css/style.css lines 79-94 */
  padding: 7px 12px 7px 12px;
  box-sizing: border-box;
  
  &::placeholder {
    color: ${({ theme }) => theme.colors.text.muted};
    font-size: inherit;
  }
  
  &:disabled {
    cursor: not-allowed;
  }
  
  /* Adjust padding when elements are present */
  ${({ $hasLeft }) => $hasLeft && 'padding-left: 0;'}
  ${({ $hasRight }) => $hasRight && 'padding-right: 0;'}
  
  /* Remove number input spinners */
  &[type="number"]::-webkit-inner-spin-button,
  &[type="number"]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  
  &[type="number"] {
    -moz-appearance: textfield;
  }
  
  /* Search input clear button styling */
  &[type="search"]::-webkit-search-cancel-button {
    -webkit-appearance: none;
  }
`;

const LeftElement = styled.div<{ theme?: Theme; $inputSize: 'sm' | 'md' | 'lg'; $variant?: 'default' | 'filled' | 'outlined' | 'display' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ theme, $variant }) => $variant === 'display' ? theme.colors.text.primary : theme.colors.text.muted};
  padding-left: 12px;
  
  ${({ $inputSize }) => $inputSize === 'sm' && 'padding-left: 8px;'}
  ${({ $inputSize }) => $inputSize === 'lg' && 'padding-left: 16px;'}
  
  /* Add right padding for display variant to space icon from text */
  ${({ $variant }) => $variant === 'display' && 'padding-right: 12px;'}
  ${({ $variant, $inputSize }) => $variant === 'display' && $inputSize === 'sm' && 'padding-right: 8px;'}
  ${({ $variant, $inputSize }) => $variant === 'display' && $inputSize === 'lg' && 'padding-right: 16px;'}
`;

const RightElement = styled.div<{ theme?: Theme; $inputSize: 'sm' | 'md' | 'lg'; $variant?: 'default' | 'filled' | 'outlined' | 'display' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ theme, $variant }) => $variant === 'display' ? theme.colors.text.primary : theme.colors.text.muted};
  padding-right: 12px;
  
  ${({ $inputSize }) => $inputSize === 'sm' && 'padding-right: 8px;'}
  ${({ $inputSize }) => $inputSize === 'lg' && 'padding-right: 16px;'}
`;


/**
 * Custom hook for debouncing values
 */
function useDebounce(
  value: string,
  timeout: number,
  callback: (value: string) => void
) {
  const callbackRef = useRef(callback);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep callback ref up to date
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (timeout <= 0) return;

    timeoutRef.current = setTimeout(() => {
      callbackRef.current(value);
    }, timeout);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [value, timeout]);
}

/**
 * Process number input - only keeps digits, dots, negative, comma, and dollar
 */
function processNumber(value: string): string {
  return value.replace(/[^\d.\-,$]/g, '');
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      type = 'text',
      size = 'md',
      variant = 'default',
      label,
      helperText,
      error = false,
      errorMessage,
      leftElement,
      rightElement,
      fullWidth = false,
      wrapperClassName,
      className,
      disabled = false,
      required = false,
      id: providedId,
      'aria-describedby': ariaDescribedBy,
      'data-testid': testId,
      value,
      onChange,
      InputProps,
      debounceTimeout = 0,
      onChangeDebounced,
      mask,
      dataId,
      showErrorMessage = true,
      inputType,
      containerClassName,
      borderRadius,
      min,
      max,
      onBlur,
      onFocus,
      inputFontSize,
      focusColor,
      ...rest
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = providedId || generatedId;
    const helperId = `${inputId}-helper`;
    const errorId = `${inputId}-error`;
    const containerRef = useRef<HTMLDivElement>(null);

    // Internal state for controlled input with debounce
    const [internalValue, setInternalValue] = useState<string>(
      value !== null && value !== undefined ? String(value) : ''
    );
    const [debounceValue, setDebounceValue] = useState<string>('');

    // Sync internal value with external value prop
    useEffect(() => {
      if (value !== null && value !== undefined) {
        const stringValue = String(value);
        setInternalValue(stringValue);
        if (debounceTimeout > 0 && onChangeDebounced) {
          setDebounceValue(stringValue);
        }
      }
    }, [value, debounceTimeout, onChangeDebounced]);

    // Debounce hook
    useDebounce(debounceValue, debounceTimeout, useCallback((newValue: string) => {
      if (onChangeDebounced && newValue !== String(value)) {
        onChangeDebounced(newValue);
      }
    }, [onChangeDebounced, value]));

    // Handle change with mask and debounce support
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      let eventValue = event.target.value;

      // Apply number processing if inputType is number
      if (inputType === 'number') {
        eventValue = processNumber(eventValue);
      }

      // Apply mask if provided
      if (mask && typeof mask === 'function') {
        eventValue = mask(eventValue);
      }

      setInternalValue(eventValue);

      // Update debounce value for debounced callback
      if (debounceTimeout > 0 && onChangeDebounced) {
        setDebounceValue(eventValue);
      }

      // Call original onChange with modified event
      if (onChange) {
        // Create modified event with masked value
        const modifiedEvent = {
          ...event,
          target: {
            ...event.target,
            value: eventValue,
          },
        };
        onChange(modifiedEvent as React.ChangeEvent<HTMLInputElement>);
      }
    };

    // Handle focus with container active state (ib-ui pattern)
    const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
      if (containerRef.current) {
        containerRef.current.classList.add('active');
      }
      onFocus?.(event);
    };

    // Handle blur with container active state removal
    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      if (containerRef.current) {
        containerRef.current.classList.remove('active');
      }
      onBlur?.(event);
    };

    // Determine start/end adornments
    // Support both leftElement/rightElement (original) and InputProps.startAdornment/endAdornment (ib-ui)
    const startAdornment = leftElement || InputProps?.startAdornment;
    const endAdornment = rightElement || InputProps?.endAdornment;

    const hasLeft = !!startAdornment;
    const hasRight = !!endAdornment;

    const displayHelperText = error && errorMessage ? errorMessage : helperText;
    const shouldShowError = error && errorMessage && showErrorMessage;
    const describedBy = [
      ariaDescribedBy,
      displayHelperText ? (error ? errorId : helperId) : undefined,
    ]
      .filter(Boolean)
      .join(' ');

    // Determine the actual value to use
    const inputValue = debounceTimeout > 0 || mask ? internalValue : value;

    // Display variant is always read-only
    const isDisplayVariant = variant === 'display';

    return (
      <Wrapper
        $fullWidth={fullWidth}
        className={wrapperClassName}
        data-testid={testId && `${testId}-wrapper`}
      >
        {label && (
          <Label htmlFor={inputId}>
            {label}
            {required && <Required aria-label="required">*</Required>}
          </Label>
        )}
        <InputContainer
          ref={containerRef}
          $inputSize={size}
          $variant={variant}
          $error={error}
          $disabled={disabled}
          $hasLeft={hasLeft}
          $hasRight={hasRight}
          $borderRadius={borderRadius}
          $focusColor={focusColor}
          className={containerClassName}
        >
          {startAdornment && (
            <LeftElement $inputSize={size} $variant={variant} aria-hidden="true">
              {startAdornment}
            </LeftElement>
          )}
          <StyledInput
            ref={ref}
            id={inputId}
            type={type}
            className={className}
            disabled={disabled}
            required={required}
            readOnly={isDisplayVariant || rest.readOnly}
            aria-invalid={error}
            aria-describedby={describedBy || undefined}
            data-testid={testId}
            data-id={dataId}
            $hasLeft={hasLeft}
            $hasRight={hasRight}
            $inputFontSize={resolveFontSize(inputFontSize)}
            $inputSize={size}
            value={inputValue}
            onChange={isDisplayVariant ? undefined : handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            min={min}
            max={max}
            {...rest}
          />
          {endAdornment && (
            <RightElement $inputSize={size} $variant={variant} aria-hidden="true">
              {endAdornment}
            </RightElement>
          )}
        </InputContainer>
        {shouldShowError ? (
          <Message
            variant="error"
            size="sm"
            dataTestId={testId ? `${testId}-error` : undefined}
          >
            {errorMessage}
          </Message>
        ) : displayHelperText ? (
          <Message
            variant="assistive"
            size="sm"
            dataTestId={testId ? `${testId}-helper` : undefined}
          >
            {displayHelperText}
          </Message>
        ) : null}
      </Wrapper>
    );
  }
);

Input.displayName = 'Input';

export default Input;
